// src/components/tiptap/extensions/noteReadingGame/useAudioManager.js
import { useEffect, useRef, useState } from "react";

/* ──────────────────────────────────────────────────────────────────────────────
   Background music (level-based)
   Tracks swap at levels: 1–2 → level1, 3–4 → level3, 5–6 → level5, 7+ → level7
   ──────────────────────────────────────────────────────────────────────────── */

const MUSIC_TRACKS = {
  level1:
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/music-level-01.mp3",
  level3:
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/music-level-02.mp3",
  level5:
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/music-level-03.mp3",
  level7:
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/music-level-04.mp3",
};

function trackKeyForLevel(level) {
  if (level >= 7) return "level7";
  if (level >= 5) return "level5";
  if (level >= 3) return "level3";
  return "level1";
}

/* ──────────────────────────────────────────────────────────────────────────────
   Level music manager
   ──────────────────────────────────────────────────────────────────────────── */

export function useLevelMusic() {
  const [isMusicEnabled, setIsMusicEnabled] = useState(true);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem("musicVolume");
    return saved
      ? Math.max(0, Math.min(1, parseInt(saved, 10) / 100))
      : 0.5;
  });

  // Audio elements live for the lifetime of this hook
  const audioRefs = useRef(null);
  const currentTrackRef = useRef("level1");
  const mountedRef = useRef(false);

  // One-time init
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    audioRefs.current = {
      level1: new Audio(MUSIC_TRACKS.level1),
      level3: new Audio(MUSIC_TRACKS.level3),
      level5: new Audio(MUSIC_TRACKS.level5),
      level7: new Audio(MUSIC_TRACKS.level7),
    };

    Object.values(audioRefs.current).forEach((a) => {
      a.loop = true;
      a.preload = "auto";
      a.volume = volume;
    });

    const handleVolumeChange = (evt) => {
      if (evt?.detail && typeof evt.detail.volume === "number") {
        setVolume(evt.detail.volume);
      }
    };

    window.addEventListener("music-volume-change", handleVolumeChange);

    return () => {
      window.removeEventListener("music-volume-change", handleVolumeChange);
      Object.values(audioRefs.current).forEach((a) => {
        try {
          a.pause();
          a.src = "";
        } catch {}
      });
      audioRefs.current = null;
    };
  }, []);

  // Apply volume changes
  useEffect(() => {
    if (!audioRefs.current) return;
    Object.values(audioRefs.current).forEach((a) => {
      a.volume = volume;
    });
  }, [volume]);

  const stopAllMusic = () => {
    if (!audioRefs.current) return;
    Object.values(audioRefs.current).forEach((a) => {
      try {
        a.pause();
        a.currentTime = 0;
      } catch {}
    });
  };

  const playMusicForLevel = async (level) => {
    if (!isMusicEnabled || !audioRefs.current) return;

    const key = trackKeyForLevel(level);
    const next = audioRefs.current[key];
    const currentKey = currentTrackRef.current;
    const current = audioRefs.current[currentKey];

    if (key === currentKey && next && !next.paused) return;

    if (current) {
      try {
        current.pause();
        current.currentTime = 0;
      } catch {}
    }

    if (next) {
      try {
        await next.play();
        currentTrackRef.current = key;
      } catch {
        // autoplay blocked → ignore
      }
    }
  };

  const toggleMusic = () => {
    setIsMusicEnabled((prev) => {
      const next = !prev;
      if (!next) {
        stopAllMusic();
      } else {
        const a = audioRefs.current?.[currentTrackRef.current];
        a?.play().catch(() => {});
      }
      return next;
    });
  };

  const setVolumeLevel = (v) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolume(clamped);
    localStorage.setItem("musicVolume", Math.round(clamped * 100));
  };

  return {
    isMusicEnabled,
    volume,
    playMusicForLevel,
    stopAllMusic,
    toggleMusic,
    setVolumeLevel,
    setEnabled: setIsMusicEnabled,
  };
}

/* ──────────────────────────────────────────────────────────────────────────────
   Lightweight SFX (correct / wrong)
   Single shared AudioContext, lazily created
   ──────────────────────────────────────────────────────────────────────────── */

let sharedSfxCtx = null;

export function useSfx() {
  const ctxRef = useRef(null);

  useEffect(() => {
    return () => {
      // Do NOT close the shared context aggressively — mobile browsers hate that
      ctxRef.current = null;
    };
  }, []);

  const ensureCtx = () => {
    if (!sharedSfxCtx) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      sharedSfxCtx = new AudioCtx();
    }
    ctxRef.current = sharedSfxCtx;
    return sharedSfxCtx;
  };

  const buzzer = (freq = 200, dur = 0.18) => {
    const ctx = ensureCtx();
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(freq, t0);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.12, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  };

  const chirp = () => {
    const ctx = ensureCtx();
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(660, t0);
    osc.frequency.exponentialRampToValueAtTime(1320, t0 + 0.15);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.12, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.22);
  };

  return (kind) => {
    if (kind === "correct") chirp();
    else if (kind === "wrong") buzzer();
  };
}